/**
 * Input validation schemas using Zod
 * Ensures all user inputs are validated to prevent injection attacks
 */
import { z } from 'zod';

// Message validation for AI calls
export const messageSchema = z.object({
  role: z.enum(['system', 'user', 'assistant']),
  content: z.string().min(1, 'Content cannot be empty').max(100000, 'Content too long')
});

export const messagesArraySchema = z.array(messageSchema)
  .min(1, 'At least one message required')
  .max(50, 'Too many messages');

// Azure OpenAI request validation
export const azureRequestSchema = z.object({
  messages: messagesArraySchema,
  deployment: z.string().min(1).max(100).optional(),
  max_completion_tokens: z.number().int().min(1).max(16000).optional(),
  temperature: z.number().min(0).max(2).optional(),
  top_p: z.number().min(0).max(1).optional()
});

// Audit creation validation
export const createAuditSchema = z.object({
  title: z.string().trim().min(1, 'Title required').max(200, 'Title too long'),
  description: z.string().trim().max(1000, 'Description too long').optional(),
  data_input: z.record(z.unknown()).default({})
});

// Organization registration validation
export const organizationSchema = z.object({
  company_name: z.string().trim().min(1).max(200),
  org_number: z.string().regex(/^\d{6}-\d{4}$/, 'Invalid org number format (XXXXXX-XXXX)'),
  billing_email: z.string().email('Invalid email'),
  billing_phone: z.string().regex(/^[\d\s\-\+\(\)]+$/, 'Invalid phone format').optional(),
  contact_email: z.string().email('Invalid email').optional()
});

// Personnummer validation
export const personnummerSchema = z.string()
  .regex(/^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}$/, 
    'Invalid personnummer format (YYYYMMDD-XXXX)');

// Team member validation
export const teamMemberSchema = z.object({
  full_name: z.string().trim().min(1).max(100),
  personnummer: personnummerSchema,
  email: z.string().email(),
  role: z.enum(['admin', 'moderator', 'user'])
});

// File upload validation
export const fileUploadSchema = z.object({
  filename: z.string().min(1).max(255),
  mime_type: z.string().regex(/^[a-z]+\/[a-z0-9\-\+\.]+$/i, 'Invalid MIME type'),
  size_bytes: z.number().int().min(1).max(100 * 1024 * 1024) // Max 100MB
});

// Export helper function for validation
export function validateInput<T>(schema: z.ZodSchema<T>, data: unknown): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const messages = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ');
      throw new Error(`Validation failed: ${messages}`);
    }
    throw error;
  }
}
